import React, { useState, useEffect } from 'react';
import { initializeTelemetry } from './services/telemetry';
import logger from './services/logger';
import { BookingSummary } from './components/BookingSummary';
import BookingForm from './components/BookingForm/BookingForm';
import { BookingData } from './components/BookingForm/types';
import { ConfirmationModal } from './components/ConfirmationModal';
import AdminDashboard from './components/AdminDashboard';
import { bookingService, type BookingRecord } from './services/graphql/bookingService';
import { Layout } from './components/Layout';
import NotFoundRedirect from './components/NotFoundRedirect';
import ProgressDialog from './components/ProgressDialog';
import MyBookingsPage from './components/MyBookingsPage';
import GoogleMapsProvider, { useGoogleMaps } from './components/GoogleMapsProvider';
import { useServiceKey } from './hooks/useServiceKey';
import { trackBookingEvent, trackError } from './services/analytics';
import AuthenticationWrapper from './components/AuthenticationWrapper';
import { useGraphQLAuth, type User } from './hooks/useGraphQLAuth';

const PWARedirectHandler: React.FC = () => {
  useEffect(() => {
    // Check if this is a PWA launch and redirect to stored service key
    const isPWALaunch = window.matchMedia('(display-mode: standalone)').matches || 
                       (window.navigator as any).standalone === true;
    
    // Check for PWA launch parameter
    const urlParams = new URLSearchParams(window.location.search);
    const isPWALaunchParam = urlParams.has('pwa_launch');
    
    if ((isPWALaunch || isPWALaunchParam) && window.location.pathname === '/') {
      const storedServiceKey = localStorage.getItem('pwa_install_service_key');
      if (storedServiceKey && storedServiceKey !== 'default') {
        // Remove the pwa_launch parameter from URL
        urlParams.delete('pwa_launch');
        const remainingParams = urlParams.toString();
        const targetUrl = `/${storedServiceKey}${remainingParams ? '?' + remainingParams : ''}`;
        logger.info('PWA redirecting to service-specific URL', { targetUrl });
        window.location.replace(targetUrl);
      } else if (isPWALaunchParam) {
        // Remove pwa_launch parameter even if no service key stored
        urlParams.delete('pwa_launch');
        const cleanUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', cleanUrl);
      }
    }
  }, []);

  return null;
};

function AppInternal() {
  const { serviceKey, serviceConfig, isValidKey, isLoading, attributes } = useServiceKey();
  const { isLoaded: isMapLoadedHook, loadError: mapLoadError } = useGoogleMaps();
  const { user: currentUser, loading: authLoading } = useGraphQLAuth();

  const [showBookingSummary, setShowBookingSummary] = useState(false);
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [pickupAddress, setPickupAddress] = useState<string>("");
  const [destinationAddress, setDestinationAddress] = useState<string>("");
  const [formMode, setFormMode] = useState<'new' | 'edit' | 'readOnly'>('new');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationTimer, setConfirmationTimer] = useState(10);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentBookingId, setCurrentBookingId] = useState<string | null>(null);
  const [confirmedBookingData, setConfirmedBookingData] = useState<BookingRecord | null>(null);
  const [showMyBookings, setShowMyBookings] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const handler = () => setShowMyBookings((prev) => !prev);
    window.addEventListener('showMyBookings', handler);
    return () => window.removeEventListener('showMyBookings', handler);
  }, []);

  useEffect(() => {
    if (serviceConfig.defaultLocation) {
      logger.debug('Initializing map center from serviceConfig', { defaultLocation: serviceConfig.defaultLocation });
    }
    if (serviceConfig.defaultZoom) {
      logger.debug('Initializing map zoom from serviceConfig', { defaultZoom: serviceConfig.defaultZoom });
    }
  }, [serviceConfig]);

  useEffect(() => {
    logger.debug('Google Maps API load status', { isLoaded: isMapLoadedHook, error: mapLoadError?.message });
    if (mapLoadError) {
      logger.error('Map loading error', mapLoadError);
    }
  }, [isMapLoadedHook, mapLoadError]);

  const handleShowBookingSummary = async (formData: BookingData) => {
    if (isSubmitting) {
      logger.warn('Submission already in progress, aborting');
      return { success: false, error: "Submission already in progress. Please wait." };
    }
    logger.debug('handleShowBookingSummary called', { bookingId: formData.bookingId });
    setIsSubmitting(true);

    if (formData.bookingId) {
      logger.debug('Using bookingId from form data', { bookingId: formData.bookingId });
      setCurrentBookingId(formData.bookingId);
      setBookingData(formData);
      setShowBookingSummary(true);
      setFormMode('readOnly');
      setIsSubmitting(false);
      logger.debug('handleShowBookingSummary finished using existing bookingId');
      return { success: true };
    } else {
      logger.error('bookingId is missing in formData in handleShowBookingSummary');
      setIsSubmitting(false);
      return { success: false, error: "Critical error: Booking ID was not available to display summary. Please try again." };
    }
  };

  const handleEditBooking = () => {
    setShowBookingSummary(false);
    setFormMode('edit');
  };
  const handleBookingConfirm = async () => {
    if (isSubmitting) {
      logger.warn('Confirmation already in progress, aborting');
      return;
    }
    logger.info('Booking confirmation initiated', { bookingId: currentBookingId });

    if (!currentBookingId) {
      setSubmitError("Booking ID is missing. Cannot confirm.");
      logger.error('Booking ID is missing in handleBookingConfirm');
      trackError("Booking ID missing in confirmation process", true);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      logger.info('Updating booking to pending_confirmation', { bookingId: currentBookingId });
      trackBookingEvent('BookingStatusChange', {
        bookingId: currentBookingId,
        status: 'pending_confirmation'
      });

      // Use confirmAnonymousBooking for anonymous bookings, confirmMyBooking for authenticated users
      const isAnonymous = bookingData?.isAnonymousBooking || !currentUser;
      const { error: updateError } = isAnonymous
        ? await bookingService.confirmAnonymousBooking(currentBookingId)
        : await bookingService.confirmMyBooking(currentBookingId);

      if (updateError) {
        logger.error('Error updating booking to pending_confirmation', undefined, { bookingId: currentBookingId });
        trackError(`Error updating booking status: ${updateError.message}`, false);
        throw updateError;
      }

      let updatedBooking: BookingRecord | null = null;
      const { error: fetchError, data: fetchedBooking } = await bookingService.getBookingById(currentBookingId);
      if (fetchError) {
        logger.warn('Failed to fetch booking after pending_confirmation', { error: fetchError.message });
        trackError(`Failed to fetch updated booking: ${fetchError.message}`, false);
      }
      if (fetchedBooking) {
        updatedBooking = fetchedBooking as BookingRecord;
        logger.info('Successfully fetched updated booking', { bookingId: currentBookingId, bookingNumber: updatedBooking.booking_number });
        setConfirmedBookingData(updatedBooking);
        trackBookingEvent('BookingConfirmed', { 
          bookingId: currentBookingId,
          bookingNumber: updatedBooking.booking_number
        });
      } else {
        throw new Error("Booking confirmation failed or details could not be retrieved.");
      }
    } catch (error: any) {
      logger.error('Error during booking confirmation', error);
      trackError(`Booking confirmation error: ${error.message}`, true);
      setSubmitError(error.message || "Failed to confirm booking. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinalConfirm = async () => {
    setShowConfirmationModal(false);
    logger.debug('Final confirmation action triggered');
  };

  const handleCancelConfirmation = () => {
    setShowConfirmationModal(false);
    setConfirmationTimer(10);
  };
  if (!isValidKey) {
    return <NotFoundRedirect isInvalidServiceKey={true} />;
  }

  if (isLoading || authLoading) {
    return (
      <ProgressDialog isOpen={isLoading || authLoading} message="Loading..." subMessage="Please wait while we load the service." />
    );
  }
  return (
    <Layout 
      currentUser={currentUser}
      serviceConfig={serviceConfig}
    >
      {showMyBookings ? (
        <MyBookingsPage />
      ) : attributes?.role === 'superadmin' ? (
        <AdminDashboard currentUser={currentUser} />
      ) : (
        <>
          {!showBookingSummary ? (
            <BookingForm 
              onShowBookingSummary={handleShowBookingSummary} 
              initialBookingData={bookingData}
              pickupAddress={pickupAddress}
              onPickupAddressChange={setPickupAddress}
              destinationAddress={destinationAddress}
              onDestinationAddressChange={setDestinationAddress}
              initialMode={formMode}
              currentUser={currentUser}            />
          ) : (
            <div className="flex justify-center items-center">
              {bookingData && (
                <BookingSummary
                  pickupLocation={bookingData.pickup}
                  dropoffLocation={bookingData.destination}
                  phoneNumber={bookingData.passengerMobile}
                  countryCode={bookingData.countryCode}
                  comfortLevel={bookingData.selectedRide}
                  estimatedDuration={bookingData.estimatedDuration}
                  email={bookingData.passengerEmail}
                  bookingId={currentBookingId || undefined}
                  bookingNumber={confirmedBookingData?.booking_number || bookingData.bookingNumber}
                  createdAt={confirmedBookingData?.created_at}
                  onBack={confirmedBookingData ? () => {} : handleEditBooking}
                  onConfirm={handleBookingConfirm}                  onCancel={
                    confirmedBookingData
                    ? () => {}
                    : () => {
                        trackBookingEvent('BookingCancelled', { bookingId: currentBookingId });
                        setShowBookingSummary(false);
                        setBookingData(null);
                        setCurrentBookingId(null);
                        setFormMode('new');
                        setSubmitError(null);
                      }
                  }                  onDone={() => {
                    trackBookingEvent('BookingFlowCompleted', { 
                      bookingId: currentBookingId,
                      bookingNumber: confirmedBookingData?.booking_number,
                      status: confirmedBookingData?.status
                    });
                    setShowBookingSummary(false);
                    setBookingData(null);
                    setCurrentBookingId(null);
                    setConfirmedBookingData(null);
                    setFormMode('new');
                    setSubmitError(null);
                  }}
                  passengerName={bookingData.passengerName}
                  pickupDate={bookingData.pickupDate}
                  pickupTime={bookingData.pickupTime}
                  timezone={serviceConfig.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                  passengers={bookingData.passengers}
                  luggage={bookingData.luggage}
                  isParentSubmitting={isSubmitting}
                  companyName={bookingData?.companyName}
                  pickupType={bookingData.pickupType}
                  paymentMethod={bookingData.paymentMethod}
                  communicationPrefs={bookingData.communicationPrefs}
                  isConfirmButtonLoading={isSubmitting}
                  bookingStatus={confirmedBookingData?.status || 'pending'}                  mode={confirmedBookingData ? 'confirmation' : 'summary'}
                  serviceKey={serviceKey}
                  isAnonymousBooking={bookingData.isAnonymousBooking}
                  generatedPassword={bookingData.generatedPassword}
                />
              )}
            </div>
          )}
          {showConfirmationModal && (
            <ConfirmationModal
              timer={confirmationTimer}
              onConfirm={handleFinalConfirm}
              onCancel={handleCancelConfirmation}
            />
          )}
        </>
      )}
      <ProgressDialog 
        isOpen={isSubmitting} 
        message="Processing your request..." 
        subMessage="Please wait while we complete your transaction." 
      />
    </Layout>
  );
}

function App() {
  useEffect(() => {
    // Initialize OpenTelemetry tracing and instrumentation
    initializeTelemetry();
  }, []);

  useEffect(() => {
    // Register service worker
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            logger.debug('Service worker registered');
            
            // Send current service key to service worker if available
            const path = window.location.pathname;
            const serviceKey = path.split('/')[1];
            if (serviceKey && serviceKey !== '' && registration.active) {
              registration.active.postMessage({
                type: 'STORE_SERVICE_KEY',
                serviceKey: serviceKey
              });
            }
          })
          .catch((registrationError) => {
            logger.warn('Service worker registration failed', { error: registrationError });
          });
      });
    }
  }, []);

  return (
    <>
      <PWARedirectHandler />
      <AppInternal />
    </>
  );
}

export default App;
